import { useState } from 'react'
import Cropper, { type Area, type Point } from 'react-easy-crop'
import { Contrast, Crop, Eraser, ImageUp, LoaderCircle, Undo2 } from 'lucide-react'
import { cropToImgStyle, type CropAreaPercent, type FieldSchema } from 'lorena-templates'
import { removeBackgroundFromImage } from '../../../../lib/renderService'

interface Props {
  field: FieldSchema
  imageUrl?: string
  originalImageUrl?: string
  transform?: CropAreaPercent | null
  onUploadFile: (file: File, originalFile?: File | null) => void | Promise<void>
  onTransformChange: (area: CropAreaPercent) => void
  uploading?: boolean
}

/**
 * Upload + recorte de imagem dentro da máscara do template (ver plano de
 * implementação, seção "Cropper de imagem"). O arquivo original é sempre
 * preservado — só o `transform` (x/y/width/height em %) é salvo, nunca uma
 * imagem já cortada, pra permitir reabrir e reeditar o enquadramento depois.
 */
export function ImageCropField({ field, imageUrl, originalImageUrl, transform, onUploadFile, onTransformChange, uploading }: Props) {
  const [editing, setEditing] = useState(false)
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)
  const [rotation, setRotation] = useState(0)
  const [sourceAspect, setSourceAspect] = useState(transform?.sourceAspect ?? 0)
  const [pendingArea, setPendingArea] = useState<CropAreaPercent | null>(null)
  const [removingBackground, setRemovingBackground] = useState(false)
  const [restoringBackground, setRestoringBackground] = useState(false)
  const [backgroundError, setBackgroundError] = useState('')
  const aspect = field.aspectRatio ?? 1
  const busy = Boolean(uploading || removingBackground || restoringBackground)

  const onFileSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) onUploadFile(file, null)
    e.target.value = ''
  }

  const openEditor = () => {
    setCrop({ x: 0, y: 0 })
    setZoom(1)
    setRotation(transform?.rotation ?? 0)
    setSourceAspect(transform?.sourceAspect ?? 0)
    setPendingArea(null)
    setEditing(true)
  }

  const applyCrop = () => {
    const area = pendingArea ?? transform ?? { x: 0, y: 0, width: 0, height: 0 }
    onTransformChange({ ...area, rotation, grayscale: transform?.grayscale ?? false, sourceAspect: sourceAspect || area.sourceAspect })
    setEditing(false)
  }

  const toggleGrayscale = () => {
    const area = transform ?? { x: 0, y: 0, width: 0, height: 0 }
    onTransformChange({ ...area, grayscale: !transform?.grayscale })
  }

  const rotateBy = (degrees: number) => {
    setRotation((current) => {
      const next = current + degrees
      return ((next + 180) % 360 + 360) % 360 - 180
    })
  }

  const makeBackgroundTransparent = async () => {
    if (!imageUrl || removingBackground) return
    setRemovingBackground(true)
    setBackgroundError('')
    try {
      const sourceResponse = await fetch(imageUrl)
      if (!sourceResponse.ok) throw new Error('Não foi possível carregar a foto original.')
      const source = await sourceResponse.blob()
      const result = await removeBackgroundFromImage(source)
      const original = new File([source], fileNameFromUrl(imageUrl, source.type), { type: source.type })
      await onUploadFile(new File([result], 'foto-sem-fundo.png', { type: 'image/png' }), original)
    } catch (err) {
      setBackgroundError(err instanceof Error ? err.message : 'Não foi possível remover o fundo da foto.')
    } finally {
      setRemovingBackground(false)
    }
  }

  const restoreOriginalBackground = async () => {
    if (!originalImageUrl || restoringBackground) return
    setRestoringBackground(true)
    setBackgroundError('')
    try {
      const response = await fetch(originalImageUrl)
      if (!response.ok) throw new Error('Não foi possível carregar a foto original.')
      const original = await response.blob()
      await onUploadFile(new File([original], fileNameFromUrl(originalImageUrl, original.type), { type: original.type }), null)
    } catch (err) {
      setBackgroundError(err instanceof Error ? err.message : 'Não foi possível restaurar o fundo original.')
    } finally {
      setRestoringBackground(false)
    }
  }

  return (
    <div className="field">
      <label>{field.label}</label>

      {imageUrl ? (
        <div
          style={{
            aspectRatio: aspect,
            width: '100%',
            maxWidth: 220,
            position: 'relative',
            overflow: 'hidden',
            backgroundColor: '#fff',
            backgroundImage:
              'linear-gradient(45deg, #e5e5e5 25%, transparent 25%), linear-gradient(-45deg, #e5e5e5 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #e5e5e5 75%), linear-gradient(-45deg, transparent 75%, #e5e5e5 75%)',
            backgroundSize: '20px 20px',
            backgroundPosition: '0 0, 0 10px, 10px -10px, -10px 0px',
            borderRadius: 4,
            border: '1px solid var(--border)',
          }}
        >
          <img src={imageUrl} alt="" style={cropStyleToReact(cropToImgStyle(transform, aspect))} />
        </div>
      ) : (
        <div
          style={{
            aspectRatio: aspect,
            width: '100%',
            maxWidth: 220,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'var(--surface)',
            border: '1px dashed var(--muted)',
            borderRadius: 4,
            color: 'var(--muted)',
            fontSize: 12,
          }}
        >
          Sem imagem
        </div>
      )}

      <div className="image-toolstrip" aria-label="Ferramentas da imagem">
        <label
          className={`image-tool-button${busy ? ' is-disabled' : ''}`}
          title={imageUrl ? 'Trocar foto' : 'Enviar foto'}
        >
          {uploading ? <LoaderCircle className="is-spinning" size={19} aria-hidden="true" /> : <ImageUp size={19} aria-hidden="true" />}
          <span className="visually-hidden">{imageUrl ? 'Trocar foto' : 'Enviar foto'}</span>
          <input type="file" accept="image/*" onChange={onFileSelected} className="visually-hidden" disabled={busy} />
        </label>
        {imageUrl && (
          <>
            <button type="button" className="image-tool-button" onClick={openEditor} disabled={busy} title="Ajustar enquadramento" aria-label="Ajustar enquadramento">
              <Crop size={19} aria-hidden="true" />
            </button>
            <button
              type="button"
              className={`image-tool-button${transform?.grayscale ? ' is-active' : ''}`}
              aria-pressed={Boolean(transform?.grayscale)}
              onClick={toggleGrayscale}
              disabled={busy}
              title={transform?.grayscale ? 'Remover preto e branco' : 'Aplicar preto e branco'}
              aria-label={transform?.grayscale ? 'Remover preto e branco' : 'Aplicar preto e branco'}
            >
              <Contrast size={19} aria-hidden="true" />
            </button>
            {originalImageUrl ? (
              <button
                type="button"
                className="image-tool-button is-active"
                onClick={restoreOriginalBackground}
                disabled={busy}
                title="Restaurar fundo original"
                aria-label="Restaurar fundo original"
              >
                {restoringBackground ? <LoaderCircle className="is-spinning" size={19} aria-hidden="true" /> : <Undo2 size={19} aria-hidden="true" />}
              </button>
            ) : (
              <button
                type="button"
                className="image-tool-button"
                onClick={makeBackgroundTransparent}
                disabled={busy}
                title="Deixar fundo transparente"
                aria-label="Deixar fundo transparente"
              >
                {removingBackground ? <LoaderCircle className="is-spinning" size={19} aria-hidden="true" /> : <Eraser size={19} aria-hidden="true" />}
              </button>
            )}
          </>
        )}
      </div>
      {(removingBackground || restoringBackground) && (
        <p className="field-help" role="status">
          {removingBackground ? 'Removendo o fundo. Na primeira vez, isso pode levar um pouco mais.' : 'Restaurando a foto original…'}
        </p>
      )}
      {backgroundError && <p style={{ margin: '8px 0 0', color: '#b3261e', fontSize: 12 }}>{backgroundError}</p>}
      {field.help && <p className="field-help">{field.help}</p>}

      {editing && imageUrl && (
        <div className="modal-backdrop" onClick={() => setEditing(false)}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 480 }}>
            <div className="modal-header">
              <h2>Ajustar enquadramento</h2>
            </div>
            <div style={{ position: 'relative', width: '100%', height: 360, background: '#111' }}>
              <Cropper
                image={imageUrl}
                crop={crop}
                zoom={zoom}
                rotation={rotation}
                aspect={aspect}
                initialCroppedAreaPercentages={transform && transform.width > 0 && transform.height > 0 ? transform : undefined}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onRotationChange={setRotation}
                onMediaLoaded={({ naturalWidth, naturalHeight }) => setSourceAspect(naturalWidth / naturalHeight)}
                onCropComplete={(area: Area) => setPendingArea({
                  ...area,
                  rotation,
                  grayscale: transform?.grayscale ?? false,
                  sourceAspect: sourceAspect || undefined,
                })}
                style={{ mediaStyle: { filter: transform?.grayscale ? 'grayscale(1)' : undefined } }}
              />
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginTop: 12 }}>
              <span style={{ fontSize: 12, color: 'var(--muted)', minWidth: 54 }}>Rotação</span>
              <button type="button" className="secondary" style={{ padding: '7px 9px' }} onClick={() => rotateBy(-90)} aria-label="Girar 90 graus para a esquerda">
                −90°
              </button>
              <input
                type="range"
                min={-180}
                max={180}
                step={1}
                value={rotation}
                onChange={(e) => setRotation(Number(e.target.value))}
                aria-label="Rotação da imagem"
                style={{ flex: 1, minWidth: 80 }}
              />
              <button type="button" className="secondary" style={{ padding: '7px 9px' }} onClick={() => rotateBy(90)} aria-label="Girar 90 graus para a direita">
                +90°
              </button>
              <output style={{ width: 42, textAlign: 'right', fontSize: 12 }}>{rotation}°</output>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 14 }}>
              <span style={{ fontSize: 12, color: 'var(--muted)' }}>Zoom</span>
              <input
                type="range"
                min={1}
                max={3}
                step={0.01}
                value={zoom}
                onChange={(e) => setZoom(Number(e.target.value))}
                style={{ flex: 1 }}
              />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <button type="button" className="secondary" onClick={() => setEditing(false)}>
                Cancelar
              </button>
              <button type="button" onClick={applyCrop}>
                Aplicar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function fileNameFromUrl(url: string, mimeType: string): string {
  const fromUrl = decodeURIComponent(url.split('?')[0].split('/').pop() || '')
  if (fromUrl.includes('.')) return fromUrl
  const extension = mimeType === 'image/png' ? 'png' : mimeType === 'image/webp' ? 'webp' : 'jpg'
  return `foto-original.${extension}`
}

function cropStyleToReact(cssText: string): React.CSSProperties {
  const style: Record<string, string> = {}
  for (const decl of cssText.split(';')) {
    const [prop, value] = decl.split(':')
    if (!prop || !value) continue
    const camel = prop.trim().replace(/-([a-z])/g, (_, c) => c.toUpperCase())
    style[camel] = value.trim()
  }
  return style as React.CSSProperties
}
